import { useState, useEffect } from 'react';
import { supabase } from '../utils/supabaseStorage';
import { parseAdminStaffSession } from '../utils/adminSession';

export interface ModelConfig {
  id: string;
  model_id: string;
  name: string;
  description: string;
  category: 'models' | 'utils' | 'effects';
  subcategory?: string;
  icon_name: string;
  icon_color: string;
  icon_bg: string;
  tags?: string[];
  fal_endpoint: string;
  credit_cost: number;
  output_type: 'image' | 'video' | 'audio';
  estimated_time?: string;
  is_available: boolean;
  coming_soon: boolean;
  featured: boolean;
  route?: string;
  input_schema: any[];
  output_mapping: any;
  sort_order?: number;
  created_at?: string;
  updated_at?: string;
  hy_user_id?: string;
  fal_stream_node_id?: string;
  fal_polling_node_name?: string;
  is_long_running?: boolean;
  polling_interval_seconds?: number;
  fal_client_mode?: 'stream' | 'queue';
  fal_queue_webhook_url?: string | null;
}

/**
 * Hook to fetch a single model by its id (UUID) or model_id (string)
 */
export const useModelById = (identifier: string) => {
  const [model, setModel] = useState<ModelConfig | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchModel = async () => {
      if (!identifier) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        // Try to fetch by UUID first (new format), then fallback to model_id (legacy)
        let query = supabase.from('modelconfig').select('*');
        
        // Check if identifier looks like a UUID
        const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(identifier);
        
        if (isUUID) {
          query = query.eq('id', identifier);
        } else {
          query = query.eq('model_id', identifier);
        }
        
        const { data, error: fetchError } = await supabase
          .from('modelconfig')
          .select('*')
          .eq(isUUID ? 'id' : 'model_id', identifier)
          .single();

        if (fetchError) {
          if (fetchError.code === 'PGRST116') {
            setError('Model not found');
          } else {
            setError(fetchError.message);
          }
          setModel(null);
        } else {
          setModel(data);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch model');
        setModel(null);
      } finally {
        setLoading(false);
      }
    };

    fetchModel();
  }, [identifier]);

  return { model, loading, error };
};

/**
 * Hook to fetch all models
 */
export const useModels = () => {
  const [models, setModels] = useState<ModelConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [staffSession, setStaffSession] = useState<ReturnType<typeof parseAdminStaffSession>>(null);

  useEffect(() => {
    setStaffSession(parseAdminStaffSession());
  }, []);

  const fetchModels = async () => {
    try {
      setLoading(true);
      setError(null);

      let query = supabase
        .from('modelconfig')
        .select('*')
        .order('sort_order', { ascending: true })
        .order('created_at', { ascending: false });

      // Apply user-level filtering for non-superadmin users
      if (staffSession && staffSession.role !== 'superadmin') {
        query = query.eq('hy_user_id', staffSession.hy_user_id);
      }

      const { data, error: fetchError } = await query;

      if (fetchError) {
        setError(fetchError.message);
        setModels([]);
      } else {
        setModels(data || []);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch models');
      setModels([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchModels();
  }, [staffSession]);

  return { models, loading, error, refetch: fetchModels };
};